/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import {useField, useFormikContext} from 'formik';
import {AutocompleteProps, Box, Checkbox} from '@mui/material';
import {BeamAutocomplete} from './BeamAutocomplete';
import TextField from '@mui/material/TextField';
import React from 'react';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {BeamInputLabel, BeamInputLabelProps} from './BeamInputLabel';
import {BeamHelpText} from "./BeamHelpText";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export interface BeamMultiSelectProps {
  name: string;
  placeholder?: string;
  options: any[];
  freeSolo?: boolean;
  helpText?: any;
  disabled?: boolean;
}

export const BeamMultiSelect = ({
  name,
  options,
  label,
  placeholder,
  freeSolo = false,
  helpText,
  optional,
  required,
  getOptionLabel,
  isOptionEqualToValue,
  sx,
  getOptionDisabled,
  disabled,
}: BeamMultiSelectProps & Omit<BeamInputLabelProps, 'htmlFor'> & Partial<AutocompleteProps<any, any, any, any>>) => {
  const formikContext = useFormikContext();
  const [field, meta, helper] = useField<string>(name);
  const { value } = field;

  return (
    <Box display="flex" flexDirection="column" gap={'8px'} sx={sx}>
      <BeamInputLabel label={label} optional={optional} required={required} htmlFor={name} />
      <BeamAutocomplete
        id={name}
        value={value ?? []}
        onChange={(e, value) => {
          helper.setValue(value);
          formikContext.validateField(name);
        }}
        onBlur={() => helper.setTouched(true, true)}
        disabled={formikContext.isSubmitting || disabled}
        autoComplete
        freeSolo={freeSolo}
        size={'small'}
        multiple
        options={options}
        disableCloseOnSelect
        getOptionDisabled={getOptionDisabled}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
            {getOptionLabel !== undefined ? getOptionLabel(option) : option}
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            onBlur={() => {
              formikContext.validateField(name);
            }}
            error={meta.touched && meta.error !== undefined}
            helperText={meta.touched && meta.error}
            placeholder={value.length === 0 ? placeholder : undefined}
          />
        )}
      />

      <BeamHelpText helpText={helpText} />
    </Box>
  );
};
